// =============================================================
// Souhlas s cookies
//
// Texty a tabulky cookies jsou právní obsah — měnit je smí jen
// se souhlasem toho, kdo za web odpovídá, a musí odpovídat
// stránce „Prohlášení o cookies“ (/prohlaseni-o-cookies).
//
// Lišta nabízí vždy všechny čtyři kategorie — nezbytné,
// preferenční, analytické a marketingové — ať se souhlas nemusí
// znovu sbírat, kdykoli v Nastavení aplikace → Analytika přibude
// měřicí nebo reklamní kód. Kategorie `analytics` a `marketing`
// hlídají kódy z app/web/Analytics.php: bez souhlasu se nespustí.
// Server v <meta name="ln-consent"> (Analytics::consentMeta)
// posílá kontaktní e-mail a zda je zapnutá reCAPTCHA.
// =============================================================

import * as CookieConsent from 'vanilla-cookieconsent';

const POLICY_URL  = '/gdpr';
const COOKIES_URL = '/prohlaseni-o-cookies';

function siteConfig() {
  try {
    return JSON.parse(document.querySelector('meta[name="ln-consent"]')?.content || '{}');
  } catch {
    return {};
  }
}

const TABLE_HEADERS = { name: 'Cookie', domain: 'Poskytovatel', desc: 'Účel', exp: 'Doba uložení' };

const esc = (s) => String(s).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));

export function initCookieConsent() {
  const cfg   = siteConfig();
  const email = cfg.email || '';

  const necessary = [
    { name: 'cc_cookie', domain: 'Náborovka', desc: 'Ukládá vaši volbu souhlasu s cookies.', exp: '6 měsíců' },
    { name: 'cms_sess', domain: 'Náborovka', desc: 'Relace — zabezpečení formulářů a základní funkce webu.', exp: 'Do zavření prohlížeče' },
  ];
  if (cfg.recaptcha) {
    necessary.push({ name: '_GRECAPTCHA', domain: 'Google (reCAPTCHA)', desc: 'Ochrana formuláře proti spamu (úložiště prohlížeče).', exp: '6 měsíců' });
  }

  const categories = {
    necessary: { readOnly: true, enabled: true },
    preferences: {},
    analytics: {},
    marketing: {},
  };

  const sections = [
    {
      title: 'Používání souborů cookies',
      description: 'Cookies jsou malé textové soubory uložené ve vašem zařízení. Níže si můžete zapnout nebo vypnout jednotlivé kategorie. Nezbytné cookies nelze vypnout, protože bez nich web nefunguje. Svůj souhlas můžete kdykoliv změnit nebo odvolat tlačítkem „Nastavení cookies“ v patičce webu.',
    },
    {
      title: 'Nezbytné <span class="pm__badge">Vždy aktivní</span>',
      description: 'Zajišťují základní funkce webu (zabezpečení, odeslání formuláře, uložení vaší volby cookies). Bez nich web nefunguje správně.',
      linkedCategory: 'necessary',
      cookieTable: { headers: TABLE_HEADERS, body: necessary },
    },
    {
      title: 'Preferenční',
      description: 'Umožňují webu zapamatovat si vaše volby (například jazyk nebo zavřená upozornění). Web je zatím nepoužívá; pokud je začne používat, uloží je jen s vaším souhlasem.',
      linkedCategory: 'preferences',
    },
    {
      title: 'Analytické',
      description: 'Pomáhají nám pochopit, jak návštěvníci web používají — které stránky jsou navštěvované a kde web vylepšit. Používáme Google Analytics 4 prostřednictvím Google Tag Manageru; bez vašeho souhlasu se nespustí.',
      linkedCategory: 'analytics',
      cookieTable: {
        headers: TABLE_HEADERS,
        body: [
          { name: '_ga', domain: 'Google (GA4)', desc: 'Rozlišení jednotlivých návštěvníků.', exp: '2 roky' },
          { name: '_ga_*', domain: 'Google (GA4)', desc: 'Udržení stavu relace v Google Analytics 4.', exp: '2 roky' },
        ],
      },
    },
    {
      title: 'Marketingové',
      description: 'Slouží k zobrazení relevantní reklamy na jiných webech a k měření její účinnosti (remarketing). Bez vašeho souhlasu se reklamní kódy nespustí.',
      linkedCategory: 'marketing',
      cookieTable: {
        headers: TABLE_HEADERS,
        body: [
          { name: '_fbp', domain: 'Meta (Facebook)', desc: 'Identifikace prohlížeče pro reklamu a remarketing.', exp: '3 měsíce' },
          { name: 'sid / ssup', domain: 'Seznam.cz (Sklik)', desc: 'Retargeting a měření konverzí v Skliku.', exp: 'až 18 měsíců' },
        ],
      },
    },
  ];

  sections.push({
    title: 'Více informací',
    description: `Podrobnosti najdete v <a href="${POLICY_URL}">Zásadách ochrany osobních údajů</a> a v <a href="${COOKIES_URL}">Prohlášení o cookies</a>.`
      + (email ? ` S dotazy se obraťte na <a href="mailto:${esc(email)}">${esc(email)}</a>.` : ''),
  });

  CookieConsent.run({
    // Zvýšit při změně kategorií nebo účelů — návštěvník se zeptá znovu.
    revision: 4,
    guiOptions: {
      consentModal: { layout: 'box', position: 'bottom right' },
      preferencesModal: { layout: 'box' },
    },
    categories,
    language: {
      default: 'cs',
      translations: {
        cs: {
          consentModal: {
            title: 'Používáme cookies',
            description: 'Soubory cookies používáme k zajištění funkčnosti webu, k zapamatování vašich voleb, k analýze návštěvnosti a k měření reklamy. Volitelné cookies použijeme jen s vaším souhlasem. Můžete přijmout všechny, odmítnout volitelné, nebo si vybrat v nastavení.',
            acceptAllBtn: 'Přijmout vše',
            acceptNecessaryBtn: 'Odmítnout volitelné',
            showPreferencesBtn: 'Nastavení',
            footer: `<a href="${POLICY_URL}">Ochrana osobních údajů</a> · <a href="${COOKIES_URL}">Prohlášení o cookies</a>`,
          },
          preferencesModal: {
            title: 'Nastavení souborů cookies',
            acceptAllBtn: 'Přijmout vše',
            acceptNecessaryBtn: 'Odmítnout volitelné',
            savePreferencesBtn: 'Uložit nastavení',
            closeIconLabel: 'Zavřít',
            serviceCounterLabel: 'Služby',
            sections,
          },
        },
      },
    },
  });
}
